import { Form, FormDate, FormInput, getFormattedCurrency } from 'metrix-common3';
import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

const ProfilePayment = (props) => {
    const intl = useIntl();

    const [_showPopup, setShowPopup] = useState(false);

    // biome-ignore lint/correctness/useExhaustiveDependencies: mount-only init
    useEffect(() => {
        props.doGetPolicy();
        props.doGetPaymentTransferResultRecords();
    }, []);

    const handleTransferFieldChange = (fieldCode, value) => {
        const updatedRecord = {
            ...props.paymentTransferResultRecords[0],
            [fieldCode]: value,
        };

        props.doReducePaymentTransferResultRecords([updatedRecord]);
    };

    const { policy, context } = props;
    const culture = context.session.culture;
    const payment = policy?.payment;

    if (!payment) {
        return (
            <React.Fragment>
                <Form
                    header={<FormattedMessage id='Profile_Payment.authorizationHeader' />}
                    label={<FormattedMessage id='Profile_Payment.authorizationInformation' />}
                    allowEdit={false}
                >
                    <FormInput
                        label={<FormattedMessage id='Profile_Payment.authorizedCreditCardChargeAmount' />}
                        value=''
                        allowEdit={false}
                    />
                    <FormInput
                        label={<FormattedMessage id='Profile_Payment.authorizationStatus' />}
                        value=''
                        allowEdit={false}
                    />
                    <FormInput
                        label={<FormattedMessage id='Profile_Payment.authorizedOn' />}
                        value=''
                        allowEdit={false}
                    />
                    <FormInput
                        label={<FormattedMessage id='Profile_Payment.authorizationExpiresOn' />}
                        value=''
                        allowEdit={false}
                    />
                </Form>
                <Form
                    header={<FormattedMessage id='Profile_Payment.sectionHeader' />}
                    label={<FormattedMessage id='Profile_Payment.formHeader' />}
                    allowEdit={false}
                >
                    <FormInput
                        label={<FormattedMessage id='Profile_Payment.paymentMode' />}
                        value=''
                        allowEdit={false}
                    />
                    <FormInput
                        label={<FormattedMessage id='Profile_Payment.paymentStatus' />}
                        value=''
                        allowEdit={false}
                    />
                    <FormInput
                        label={<FormattedMessage id='Profile_Payment.paymentDetails' />}
                        value=''
                        allowEdit={false}
                    />
                    <FormInput
                        label={<FormattedMessage id='Profile_Payment.paymentAmount' />}
                        value=''
                        allowEdit={false}
                    />
                </Form>
            </React.Fragment>
        );
    }

    const paymentMode = payment?.paymentMode !== 'none' ? payment.paymentMode : null;
    const currency = payment?.currencyCode;

    const isFullPayment = paymentMode === 'fullPayment';
    const isPartialPayment = paymentMode === 'installmentPayment';

    const fullPaymentSuccessful =
        isFullPayment && (payment.creditCardFullChargeSuccessful || payment.isAuthorizationCharged);
    const fullPaymentAmount = getFormattedCurrency(payment.creditCardFullChargeAmount, currency, culture);

    const downPaymentReceived = isPartialPayment && payment.creditCardPartialChargeSuccessful;
    const monthlySubscriptionCreated = isPartialPayment && payment.debitSetupIntentSuccessful;
    const downPaymentCharged = getFormattedCurrency(payment.creditCardPartialChargeAmount, currency, culture);
    const monthlyAmount = getFormattedCurrency(payment.debitCycleChargeAmount, currency, culture);
    const startDate = payment.debitCycleChargeDay;

    const isSuccess = fullPaymentSuccessful || (downPaymentReceived && monthlySubscriptionCreated);

    // Use intl.formatMessage for strings that will be used as values in FormInput
    let paymentDetails = '';
    if (fullPaymentSuccessful)
        paymentDetails = intl.formatMessage({ id: 'Profile_Payment.paymentCompletedFullAmount' });
    if (isPartialPayment) {
        if (downPaymentReceived) paymentDetails = intl.formatMessage({ id: 'Profile_Payment.downpaymentReceived' });
        if (monthlySubscriptionCreated)
            paymentDetails = intl.formatMessage({ id: 'Profile_Payment.monthlySubscriptionSuccess' });
    }

    const failedReasonDebit1 = payment.initiateDebitSetupIntentFailedReason;
    const failedReasonDebit2 = payment.debitSetupIntentFailedReason;
    const cumulativeFailedReason =
        failedReasonDebit1 && failedReasonDebit2
            ? `${failedReasonDebit1}; ${failedReasonDebit2}`
            : failedReasonDebit1
              ? failedReasonDebit1
              : failedReasonDebit2
                ? failedReasonDebit2
                : '';

    const isPaymentTransferResultReadOnly = props.paymentTransferResultReadOnly;
    const isPaymentTransferResultHidden = props.paymentTransfersResultHidden;
    const paymentTransferResultErrors = props.paymentTransferResultErrors?.map((error) => ({
        ...error,
        value: error.code,
    }));

    const validate = () => {
        return (
            !Number.isNaN(Number.parseFloat(props.paymentTransferResultRecords[0]?.transferedAmount)) &&
            props.paymentTransferResultRecords[0]?.transferedOn &&
            props.paymentTransferResultRecords[0]?.transferReference
        );
    };

    const handleSavePaymentTransfer = () => {
        if (validate()) {
            props.doCreatePaymentTransferResultRecord();
        } else {
            setShowPopup(true);
        }
    };

    const handleCancelPaymentTransfer = () => {
        props.doGetPaymentTransferResultRecords();
    };

    // Get success/fail status as string
    // Use backend-provided paymentStatusText if available, otherwise calculate locally
    const paymentStatusText =
        payment.paymentStatusText ||
        (isSuccess
            ? intl.formatMessage({ id: 'Profile_Payment.success' })
            : intl.formatMessage({ id: 'Profile_Payment.incomplete' }));

    // Authorization section data
    const hasAuthorization = payment?.hasAuthorization || false;
    const isAuthorizationExpired = payment?.isAuthorizationExpired;
    const showAuthorizationSection = hasAuthorization && isAuthorizationExpired === false;

    const authorizationStatusFormatted = payment?.authorizationStatusFormatted || '';
    const authorizationFailedReason = payment?.authorizationFailedReason || '';
    const authorizedCreditCardChargeAmountFormatted = payment?.authorizedCreditCardChargeAmountFormatted || '';
    const authorizedOnFormatted = payment?.authorizedOnFormatted || '';
    const authorizationExpiresOnFormatted = payment?.authorizationExpiresOnFormatted || '';
    const isAuthorizationSuccess =
        payment?.authorizationStatus && payment.authorizationStatus.toLowerCase() === 'active';

    return (
        <React.Fragment>
            {showAuthorizationSection && (
                <Form
                    header={<FormattedMessage id='Profile_Payment.authorizationHeader' />}
                    label={<FormattedMessage id='Profile_Payment.authorizationInformation' />}
                    allowEdit={false}
                >
                    {authorizedCreditCardChargeAmountFormatted && (
                        <FormInput
                            label={<FormattedMessage id='Profile_Payment.authorizedCreditCardChargeAmount' />}
                            value={authorizedCreditCardChargeAmountFormatted}
                            allowEdit={false}
                        />
                    )}
                    {authorizationStatusFormatted && (
                        <FormInput
                            label={<FormattedMessage id='Profile_Payment.authorizationStatus' />}
                            value={authorizationStatusFormatted}
                            allowEdit={false}
                        />
                    )}
                    {!isAuthorizationSuccess && authorizationFailedReason && (
                        <FormInput
                            label={<FormattedMessage id='Profile_Payment.authorizationFailedReason' />}
                            value={authorizationFailedReason}
                            allowEdit={false}
                        />
                    )}
                    {authorizedOnFormatted && (
                        <FormInput
                            label={<FormattedMessage id='Profile_Payment.authorizedOn' />}
                            value={authorizedOnFormatted}
                            allowEdit={false}
                        />
                    )}
                    {authorizationExpiresOnFormatted && (
                        <FormInput
                            label={<FormattedMessage id='Profile_Payment.authorizationExpiresOn' />}
                            value={authorizationExpiresOnFormatted}
                            allowEdit={false}
                        />
                    )}
                </Form>
            )}
            <Form
                header={<FormattedMessage id='Profile_Payment.sectionHeader' />}
                label={<FormattedMessage id='Profile_Payment.formHeader' />}
                saveEvent={props.doUpsertPolicy}
                cancelEvent={props.doGetPolicy}
                allowEdit
            >
                {paymentMode && (
                    <FormInput
                        label={<FormattedMessage id='Profile_Payment.paymentMode' />}
                        value={payment.paymentModeText}
                        allowEdit={false}
                    />
                )}
                {paymentMode && (
                    <FormInput
                        label={<FormattedMessage id='Profile_Payment.paymentStatus' />}
                        value={paymentStatusText}
                        allowEdit={false}
                    />
                )}
                {paymentDetails && (
                    <FormInput
                        label={<FormattedMessage id='Profile_Payment.paymentDetails' />}
                        value={paymentDetails}
                        allowEdit={false}
                    />
                )}
                {paymentMode && isFullPayment && (
                    <FormInput
                        label={<FormattedMessage id='Profile_Payment.paymentAmount' />}
                        value={fullPaymentAmount}
                        allowEdit={false}
                    />
                )}
                {paymentMode && isPartialPayment && (
                    <React.Fragment>
                        {payment.creditCardFullChargeAmount && (
                            <FormInput
                                label={<FormattedMessage id='Profile_Payment.downpaymentAmount' />}
                                value={downPaymentCharged}
                                allowEdit={false}
                            />
                        )}
                        {payment.debitCycleChargeAmount && (
                            <FormInput
                                label={<FormattedMessage id='Profile_Payment.monthlyAmount' />}
                                value={monthlyAmount}
                                allowEdit={false}
                            />
                        )}
                        {startDate && (
                            <FormInput
                                label={<FormattedMessage id='Profile_Payment.startDateMonthlyCharges' />}
                                value={startDate}
                                allowEdit={false}
                            />
                        )}
                    </React.Fragment>
                )}
                {!isSuccess && cumulativeFailedReason && (
                    <FormInput
                        label={<FormattedMessage id='Profile_Payment.failedReasonDebit' />}
                        value={cumulativeFailedReason}
                        allowEdit={false}
                    />
                )}
                <FormDate
                    label={<FormattedMessage id='Profile_Payment.receivePaymentExpiryDate' />}
                    fieldCode='payment.receivePaymentExpiryDate'
                    value={payment.receivePaymentExpiryDate || ''}
                    changeEvent={(value) =>
                        props.doReducePolicyElement({
                            code: 'payment.receivePaymentExpiryDate',
                            value: value,
                        })
                    }
                    culture={props.context.session.culture}
                    allowEdit
                />
            </Form>
            {!isPaymentTransferResultHidden && (
                <Form
                    header={<FormattedMessage id='Profile_Payment.paymentTransfer' />}
                    label={<FormattedMessage id='Profile_Payment.transferDetails' />}
                    sectionCode='paymentTransfer'
                    saveEvent={handleSavePaymentTransfer}
                    cancelEvent={handleCancelPaymentTransfer}
                    pageCode={props.pageCode}
                    saveTooltipInfo={() => props.doUpsertTooltipInfo(props.policy.product)}
                    reduceTooltipInfoElement={props.doReduceTooltipInfoElement}
                    tooltipInfo={props.tooltipInfo}
                    validationInfo={props.validationInfo}
                    allowEdit={!isPaymentTransferResultReadOnly}
                    serverErrors={paymentTransferResultErrors}
                    validate={validate}
                >
                    <FormInput
                        label={<FormattedMessage id='Profile_Payment.transferredAmount' />}
                        fieldCode='transferedAmount'
                        value={props.paymentTransferResultRecords[0]?.transferedAmount || ''}
                        type='currency'
                        withCents={true}
                        changeEvent={(value) => handleTransferFieldChange('transferedAmount', value)}
                        allowEdit
                    />
                    <FormDate
                        label={<FormattedMessage id='Profile_Payment.transferredDate' />}
                        fieldCode='transferedOn'
                        value={props.paymentTransferResultRecords[0]?.transferedOn || ''}
                        changeEvent={(value) => handleTransferFieldChange('transferedOn', value)}
                        culture={props.context.session.culture}
                        allowEdit
                    />
                    <FormInput
                        label={<FormattedMessage id='Profile_Payment.transferReferenceCode' />}
                        fieldCode='transferReference'
                        value={props.paymentTransferResultRecords[0]?.transferReference || ''}
                        type='text'
                        changeEvent={(value) => handleTransferFieldChange('transferReference', value)}
                        allowEdit
                    />
                </Form>
            )}
        </React.Fragment>
    );
};

export default ProfilePayment;
